<template>
  <div class="closing-history-page">
    <!-- 헤더 -->
    <div class="page-header">
      <div class="title-area">
        <h2 class="page-title">마감요청내역</h2>
        <p class="page-subtitle">월별 계정조정 마감 및 희망이음 업로드 현황</p>
      </div>
      <div class="header-actions">
        <button class="btn-refresh" @click="() => loadData()" :disabled="isLoading">
          <v-icon size="18">mdi-refresh</v-icon>
          새로고침
        </button>
      </div>
    </div>

    <!-- 통계 요약 -->
    <div class="stats-bar">
      <div class="stat-item">
        <span class="stat-value">{{ summaryStats.totalPeriods }}</span>
        <span class="stat-label">전체</span>
      </div>
      <div class="stat-divider"></div>
      <div class="stat-item pending">
        <span class="stat-value">{{ summaryStats.pendingPeriods }}</span>
        <span class="stat-label">조정 중</span>
      </div>
      <div class="stat-item ready">
        <span class="stat-value">{{ summaryStats.readyPeriods }}</span>
        <span class="stat-label">마감가능</span>
      </div>
      <div class="stat-item closed">
        <span class="stat-value">{{ summaryStats.closedPeriods }}</span>
        <span class="stat-label">마감완료</span>
      </div>
      <div class="stat-item requested">
        <span class="stat-value">{{ summaryStats.requestedPeriods }}</span>
        <span class="stat-label">요청중</span>
      </div>
      <div class="stat-item completed">
        <span class="stat-value">{{ summaryStats.completedPeriods }}</span>
        <span class="stat-label">완료</span>
      </div>
    </div>

    <!-- 테이블 -->
    <div class="table-card" ref="tableCardRef">
      <table class="closing-table sortable">
        <thead>
          <tr>
            <th class="col-period sortable-th" @click="toggleSort('period')">
              연월
              <v-icon size="14" class="sort-icon" :class="getSortClass('period')">
                {{ getSortIcon('period') }}
              </v-icon>
            </th>
            <th class="col-bank sortable-th" @click="toggleSort('bank')">
              통장
              <v-icon size="14" class="sort-icon" :class="getSortClass('bank')">
                {{ getSortIcon('bank') }}
              </v-icon>
            </th>
            <th class="col-adjust sortable-th" @click="toggleSort('adjust')">
              계정조정
              <v-icon size="14" class="sort-icon" :class="getSortClass('adjust')">
                {{ getSortIcon('adjust') }}
              </v-icon>
            </th>
            <th class="col-amount sortable-th" @click="toggleSort('income')">
              수입
              <v-icon size="14" class="sort-icon" :class="getSortClass('income')">
                {{ getSortIcon('income') }}
              </v-icon>
            </th>
            <th class="col-amount sortable-th" @click="toggleSort('expense')">
              지출
              <v-icon size="14" class="sort-icon" :class="getSortClass('expense')">
                {{ getSortIcon('expense') }}
              </v-icon>
            </th>
            <th class="col-status sortable-th" @click="toggleSort('accountStatus')">
              계정조정
              <v-icon size="14" class="sort-icon" :class="getSortClass('accountStatus')">
                {{ getSortIcon('accountStatus') }}
              </v-icon>
            </th>
            <th class="col-status sortable-th" @click="toggleSort('heumieumStatus')">
              희망이음
              <v-icon size="14" class="sort-icon" :class="getSortClass('heumieumStatus')">
                {{ getSortIcon('heumieumStatus') }}
              </v-icon>
            </th>
            <th class="col-memo">메모</th>
            <th class="col-history">이력</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="isLoading">
            <td colspan="9" class="loading-row">
              <v-progress-circular indeterminate size="24" width="2" color="#1e3a5f" />
              <span>데이터 로딩중...</span>
            </td>
          </tr>
          <tr v-else-if="summaryList.length === 0">
            <td colspan="9" class="empty-row">
              <v-icon size="40" color="#d1d5db">mdi-file-document-outline</v-icon>
              <p>통장내역 데이터가 없습니다.</p>
            </td>
          </tr>
          <template v-else>
            <tr 
              v-for="(item, index) in sortedList" 
              :key="`${item.bankCode}_${item.year}_${item.month}`"
              :class="[getRowClass(item), { 'period-first': isFirstInPeriod(item, index), 'period-last': isLastInPeriod(item, index) }]"
            >
              <td 
                v-if="isFirstInPeriod(item, index)" 
                class="col-period"
                :rowspan="getPeriodRowCount(item)"
              >
                <span class="period-text">{{ item.year }}.{{ String(item.month).padStart(2, '0') }}</span>
              </td>
              <td class="col-bank">
                <div class="bank-info">
                  <span class="bank-alias">{{ item.bankAlias }}</span>
                  <span v-if="item.bankName" class="bank-name">{{ item.bankName }}</span>
                </div>
              </td>
              <td class="col-adjust">
                <div class="adjust-progress" :class="{ 'all-complete': item.adjustedCount === item.totalCount }">
                  <div class="progress-bar">
                    <div 
                      class="progress-fill" 
                      :style="{ width: getAdjustPercent(item) + '%' }"
                      :class="{ complete: item.adjustedCount === item.totalCount }"
                    ></div>
                  </div>
                  <span class="progress-text" :class="{ complete: item.adjustedCount === item.totalCount }">
                    {{ item.adjustedCount }}/{{ item.totalCount }}
                  </span>
                  <v-icon 
                    v-if="item.adjustedCount === item.totalCount" 
                    size="16" 
                    class="complete-icon"
                  >mdi-check-circle</v-icon>
                </div>
              </td>
              <td class="col-amount income">
                {{ formatNumber(item.totalIncome) }}
              </td>
              <td class="col-amount expense">
                {{ formatNumber(item.totalExpense) }}
              </td>
              <td class="col-status">
                <select 
                  class="status-select"
                  :class="item.accountAdjustClosed ? 'closed' : 'pending'"
                  :value="item.accountAdjustClosed ? 'closed' : 'pending'"
                  @change="changeAccountStatus(item, ($event.target as HTMLSelectElement).value)"
                  :disabled="isProcessing"
                >
                  <option value="pending">조정중</option>
                  <option value="closed">마감완료</option>
                </select>
              </td>
              <td class="col-status">
                <select 
                  class="status-select"
                  :class="getHeumieumSelectClass(item)"
                  :value="getHeumieumSelectValue(item)"
                  @change="changeHeumieumStatus(item, ($event.target as HTMLSelectElement).value)"
                  :disabled="isProcessing || !item.accountAdjustClosed"
                  :title="getHeumieumTitle(item)"
                >
                  <option v-if="!item.heumieumStatus" value="" disabled hidden></option>
                  <option value="requested">등록요청</option>
                  <option value="completed" :disabled="!item.heumieumStatus">완료</option>
                </select>
              </td>
              <td class="col-memo">
                <div class="memo-cell">
                  <span v-if="item.memo" class="memo-text">{{ item.memo }}</span>
                  <button 
                    class="memo-btn"
                    @click="openMemoDialog(item)"
                    :title="item.memo ? '메모 수정' : '메모 작성'"
                  >
                    <v-icon size="14">{{ item.memo ? 'mdi-pencil' : 'mdi-plus' }}</v-icon>
                  </button>
                </div>
              </td>
              <td class="col-history">
                <button 
                  class="history-btn"
                  @click="openHistoryDialog(item)"
                  title="이력 보기"
                >
                  <v-icon size="14">mdi-history</v-icon>
                </button>
              </td>
            </tr>
          </template>
        </tbody>
      </table>
    </div>

    <!-- 메모 다이얼로그 -->
    <v-dialog v-model="memoDialog.show" max-width="450">
      <v-card class="memo-dialog">
        <v-card-title class="dialog-title">
          <v-icon size="20">mdi-note-text</v-icon>
          메모
          <span class="dialog-subtitle">{{ memoDialog.year }}.{{ String(memoDialog.month).padStart(2, '0') }} {{ memoDialog.bankAlias }}</span>
          <button
            type="button"
            class="dialog-close"
            aria-label="닫기"
            :disabled="isSavingMemo"
            @click="closeMemoDialog"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="dialog-content">
          <v-text-field
            v-model="memoDialog.memo"
            placeholder="메모를 입력 후 엔터"
            variant="outlined"
            hide-details
            density="comfortable"
            autofocus
            @keydown.enter.prevent="saveMemo"
          />
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn variant="text" class="btn-cancel" @click="closeMemoDialog">취소</v-btn>
          <v-spacer />
          <v-btn 
            variant="flat" 
            class="btn-save" 
            @click="saveMemo"
            :loading="isSavingMemo"
          >
            저장
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 이력 다이얼로그 -->
    <v-dialog v-model="historyDialog.show" max-width="650">
      <v-card class="history-dialog">
        <v-card-title class="dialog-title">
          <v-icon size="20">mdi-history</v-icon>
          희망이음 요청 이력
          <span class="dialog-subtitle">{{ historyDialog.year }}.{{ String(historyDialog.month).padStart(2, '0') }} {{ historyDialog.bankAlias }}</span>
          <button
            type="button"
            class="dialog-close"
            aria-label="닫기"
            :disabled="isLoadingHistory"
            @click="closeHistoryDialog"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="dialog-content">
          <div v-if="isLoadingHistory" class="history-loading">
            <v-progress-circular indeterminate size="24" width="2" color="#1e3a5f" />
            <span>이력 로딩중...</span>
          </div>
          <div v-else-if="historyList.length === 0" class="history-empty">
            <v-icon size="32" color="#d1d5db">mdi-history</v-icon>
            <p>이력이 없습니다.</p>
          </div>
          <div v-else class="history-list">
            <div 
              v-for="h in historyList" 
              :key="h.idx" 
              class="history-item"
              :class="`action-${h.action}`"
            >
              <div class="history-icon">
                <v-icon size="16">{{ getHistoryIcon(h.action) }}</v-icon>
              </div>
              <div class="history-content">
                <div class="history-main">
                  <span class="history-action">{{ getHistoryActionLabel(h.action, h.requestType) }}</span>
                  <span v-if="h.createdByName" class="history-user">{{ h.createdByName }}</span>
                </div>
                <div class="history-time">{{ formatDateTime(h.createdAt) }}</div>
                <div v-if="h.memo" class="history-memo">{{ h.memo }}</div>
              </div>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-spacer />
          <v-btn variant="text" class="btn-close" @click="closeHistoryDialog">닫기</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, inject, watch, type Ref } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { closingApi } from '@/services/api';

// 희망이음 요청 타입 정의
type HeumieumRequestType = 'register' | 'delete' | 'modify'; 

const HEUMIEUM_REQUEST_TYPES: Record<HeumieumRequestType, { label: string; shortLabel: string }> = {
  register: { label: '등록요청', shortLabel: '등록' },
  delete: { label: '삭제요청', shortLabel: '삭제' },
  modify: { label: '수정요청', shortLabel: '수정' }
};

interface SummaryItem {
  bankCode: string;
  bankAlias: string;
  bankName: string;
  year: number;
  month: number;
  totalCount: number;
  adjustedCount: number;
  totalIncome: number;
  totalExpense: number;
  accountAdjustClosed: boolean;
  accountAdjustClosedAt: string | null;
  heumieumStatus: 'requested' | 'completed' | null;
  heumieumRequestType: HeumieumRequestType | null;
  heumieumRequestAt: string | null;
  memo: string | null;
}

const router = useRouter();
const route = useRoute();

const memberData = inject<any>('memberData');
const selectedBizGubunRef = inject<Ref<string>>('selectedBizGubun');
const accountingBizGubun = computed(() => selectedBizGubunRef?.value || '');

const memberCode = computed(() => {
  return route.params.memberCode as string || String(memberData?.value?.m_code || '');
});

const isLoading = ref(false);
const isProcessing = ref(false);
const isSavingMemo = ref(false);
const summaryList = ref<SummaryItem[]>([]);

// 테이블 스크롤 위치 유지
const tableCardRef = ref<HTMLDivElement | null>(null);
let savedScrollTop = 0;

const saveScrollPosition = () => {
  if (tableCardRef.value) {
    savedScrollTop = tableCardRef.value.scrollTop;
  }
};

const restoreScrollPosition = () => {
  if (tableCardRef.value) {
    tableCardRef.value.scrollTop = savedScrollTop;
  }
};

// 정렬 상태
type SortKey = 'period' | 'bank' | 'adjust' | 'income' | 'expense' | 'accountStatus' | 'heumieumStatus';
type SortDir = 'asc' | 'desc' | null;

const sortKey = ref<SortKey | null>(null);
const sortDir = ref<SortDir>(null);

// 메모 다이얼로그
const memoDialog = ref({
  show: false,
  year: 0,
  month: 0,
  bankCode: '',
  bankAlias: '',
  memo: ''
});

// 이력 다이얼로그
interface HistoryItem {
  idx: number;
  ccIdx: number;
  action: string;
  requestType: string | null;
  prevStatus: string | null;
  newStatus: string | null;
  year: number;
  month: number;
  bankCode: string;
  memo: string | null;
  createdAt: string;
  createdByName: string | null;
}

const historyDialog = ref({
  show: false,
  year: 0,
  month: 0,
  bankCode: '',
  bankAlias: ''
});
const historyList = ref<HistoryItem[]>([]);
const isLoadingHistory = ref(false);

const summaryStats = computed(() => {
  const list = summaryList.value;
  return {
    totalPeriods: list.length,
    pendingPeriods: list.filter(i => i.adjustedCount < i.totalCount).length,
    readyPeriods: list.filter(i => i.adjustedCount === i.totalCount && !i.accountAdjustClosed).length,
    closedPeriods: list.filter(i => i.accountAdjustClosed && !i.heumieumStatus).length,
    requestedPeriods: list.filter(i => i.heumieumStatus === 'requested').length,
    completedPeriods: list.filter(i => i.heumieumStatus === 'completed').length
  };
});

// 정렬된 리스트
const sortedList = computed(() => {
  let list = [...summaryList.value];
  
  if (sortKey.value && sortDir.value) {
    const dir = sortDir.value === 'asc' ? 1 : -1;
    
    list = list.sort((a, b) => {
      switch (sortKey.value) {
        case 'period':
          const periodA = a.year * 100 + a.month;
          const periodB = b.year * 100 + b.month;
          return (periodA - periodB) * dir;
        
        case 'bank':
          return a.bankAlias.localeCompare(b.bankAlias) * dir;
        
        case 'adjust':
          const rateA = a.totalCount > 0 ? a.adjustedCount / a.totalCount : 0;
          const rateB = b.totalCount > 0 ? b.adjustedCount / b.totalCount : 0;
          return (rateA - rateB) * dir;
        
        case 'income':
          return (a.totalIncome - b.totalIncome) * dir;
        
        case 'expense':
          return (a.totalExpense - b.totalExpense) * dir;
        
        case 'accountStatus':
          const statusA = a.accountAdjustClosed ? 2 : (a.adjustedCount === a.totalCount ? 1 : 0);
          const statusB = b.accountAdjustClosed ? 2 : (b.adjustedCount === b.totalCount ? 1 : 0);
          return (statusA - statusB) * dir;
        
        case 'heumieumStatus':
          const hStatusMap: Record<string, number> = { 'completed': 3, 'requested': 2 };
          const hStatusA = a.heumieumStatus ? hStatusMap[a.heumieumStatus] : (a.accountAdjustClosed ? 1 : 0);
          const hStatusB = b.heumieumStatus ? hStatusMap[b.heumieumStatus] : (b.accountAdjustClosed ? 1 : 0);
          return (hStatusA - hStatusB) * dir;
        
        default:
          return 0;
      }
    });
  }
  
  return list;
});

// 연월 그룹 정보 계산
const periodGroups = computed(() => {
  const groups = new Map<string, number>();
  sortedList.value.forEach(item => {
    const key = `${item.year}_${item.month}`;
    groups.set(key, (groups.get(key) || 0) + 1);
  });
  return groups;
});

// 연월 그룹의 첫 번째 행인지 확인
const isFirstInPeriod = (item: SummaryItem, index: number): boolean => {
  if (index === 0) return true;
  const prev = sortedList.value[index - 1];
  return item.year !== prev.year || item.month !== prev.month;
};

// 연월 그룹의 마지막 행인지 확인
const isLastInPeriod = (item: SummaryItem, index: number): boolean => {
  if (index === sortedList.value.length - 1) return true;
  const next = sortedList.value[index + 1];
  return item.year !== next.year || item.month !== next.month;
};

// 해당 연월의 행 개수
const getPeriodRowCount = (item: SummaryItem): number => {
  const key = `${item.year}_${item.month}`;
  return periodGroups.value.get(key) || 1;
};

// 정렬 토글
const toggleSort = (key: SortKey) => {
  if (sortKey.value === key) {
    if (sortDir.value === 'asc') {
      sortDir.value = 'desc';
    } else if (sortDir.value === 'desc') {
      sortKey.value = null;
      sortDir.value = null;
    }
  } else {
    sortKey.value = key;
    sortDir.value = 'asc';
  }
};

const getSortIcon = (key: SortKey): string => {
  if (sortKey.value !== key) return 'mdi-unfold-more-horizontal';
  if (sortDir.value === 'asc') return 'mdi-arrow-up';
  if (sortDir.value === 'desc') return 'mdi-arrow-down';
  return 'mdi-unfold-more-horizontal';
};

const getSortClass = (key: SortKey): string => {
  return sortKey.value === key && sortDir.value ? 'active' : '';
};

const formatNumber = (num: number | string | null | undefined): string => {
  if (num === null || num === undefined || num === '') return '0';
  const n = Number(num);
  if (isNaN(n)) return '0';
  return n.toLocaleString('ko-KR');
};

const getAdjustPercent = (item: SummaryItem): number => {
  if (item.totalCount === 0) return 0;
  return Math.round((item.adjustedCount / item.totalCount) * 100);
};

const getRowClass = (item: SummaryItem): string => {
  if (item.heumieumStatus === 'completed') return 'row-completed';
  if (item.heumieumStatus === 'requested') return 'row-requested';
  if (item.accountAdjustClosed) return 'row-closed';
  if (item.adjustedCount === item.totalCount) return 'row-ready';
  return 'row-pending';
};

const loadData = async (preserveScroll = false) => {
  if (!memberCode.value) return;
  
  if (!preserveScroll) {
    isLoading.value = true;
  }
  
  try {
    const response = await closingApi.getSummary(memberCode.value, {
      bizGubun: accountingBizGubun.value
    });
    
    if (response.data?.success) {
      summaryList.value = response.data.data.summary || [];
    }
  } catch (error: any) {
    const message = error.response?.data?.message || error.message || '알 수 없는 오류';
    alert(`마감요청내역 조회 실패: ${message}`);
    console.error('마감요청내역 조회 오류:', error);
  } finally {
    if (!preserveScroll) {
      isLoading.value = false;
    }
  }
};

// 계정조정 상태 변경 (셀렉트)
const changeAccountStatus = async (item: SummaryItem, newStatus: string) => {
  if (!memberCode.value) return;
  
  const currentStatus = item.accountAdjustClosed ? 'closed' : 'pending';
  if (currentStatus === newStatus) return;
  
  if (newStatus === 'closed') {
    // 마감 처리
    isProcessing.value = true;
    try {
      const response = await closingApi.close(memberCode.value, {
        year: item.year,
        month: item.month,
        type: 'bankbook',
        status: 'account_adjust',
        bizGubun: accountingBizGubun.value,
        bankCode: item.bankCode
      });
      if (response.data?.success) {
        await loadData(true);
      } else {
        throw new Error(response.data?.message || '마감 처리 실패');
      }
    } catch (error: any) {
      alert(`오류: ${error.response?.data?.message || error.message}`);
      await loadData(true); // 실패 시 원래 상태로 복원
    } finally {
      isProcessing.value = false;
    }
  } else {
    // 마감 해제
    // 희망이음 상태가 있으면 경고 후 확인
    if (item.heumieumStatus) {
      const statusLabel = item.heumieumStatus === 'completed' ? '등록완료' : '요청중';
      const confirmed = confirm(
        `현재 희망이음 "${statusLabel}" 상태입니다.\n` +
        `마감을 해제하면 희망이음 상태도 함께 초기화됩니다.\n\n` +
        `정말 마감을 해제하시겠습니까?`
      );
      if (!confirmed) {
        await loadData(true);
        return;
      }
    }
    
    isProcessing.value = true;
    try {
      const response = await closingApi.unclose(memberCode.value, {
        year: item.year,
        month: item.month,
        type: 'bankbook',
        status: 'account_adjust',
        bizGubun: accountingBizGubun.value,
        bankCode: item.bankCode
      });
      if (response.data?.success) {
        await loadData(true);
      } else {
        throw new Error(response.data?.message || '마감 해제 실패');
      }
    } catch (error: any) {
      alert(`오류: ${error.response?.data?.message || error.message}`);
      await loadData(true); // 실패 시 원래 상태로 복원
    } finally {
      isProcessing.value = false;
    }
  }
};

// 희망이음 셀렉트 클래스
const getHeumieumSelectClass = (item: SummaryItem): string => {
  if (item.heumieumStatus === 'completed') return 'completed-editable';
  if (item.heumieumStatus === 'requested') return 'requested request-type-register';
  if (!item.accountAdjustClosed) return 'disabled';
  return 'pending';
};

const getHeumieumSelectValue = (item: SummaryItem): string => {
  if (item.heumieumStatus === 'completed') return 'completed';
  if (item.heumieumStatus === 'requested') return 'requested';
  return '';
};

const getHeumieumTitle = (item: SummaryItem): string => {
  if (!item.accountAdjustClosed) return '계정조정 마감 후 선택 가능';
  if (item.heumieumStatus === 'requested') return '등록요청 진행중';
  if (item.heumieumStatus === 'completed') return '완료 - 등록요청으로 바꾸면 다시 등록할 수 있습니다';
  return '';
};

// 요청 타입 라벨
const getRequestTypeLabel = (requestType: HeumieumRequestType | null): string => {
  if (!requestType) return '등록';
  return HEUMIEUM_REQUEST_TYPES[requestType]?.shortLabel || requestType;
};

// 희망이음 상태 변경 (셀렉트)
const changeHeumieumStatus = async (item: SummaryItem, newValue: string) => {
  if (!memberCode.value) return;

  const currentValue = getHeumieumSelectValue(item);
  if (currentValue === newValue) return;

  isProcessing.value = true;

  try {
    if (newValue === 'requested') {
      const response = await closingApi.heumieumRequest(memberCode.value, {
        year: item.year,
        month: item.month,
        bizGubun: accountingBizGubun.value,
        bankCode: item.bankCode,
        bankAlias: item.bankAlias,
        requestType: 'register'
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || '등록요청 실패');
      }
    } else if (newValue === 'completed') {
      const response = await closingApi.heumieumComplete(memberCode.value, {
        year: item.year,
        month: item.month,
        bankCode: item.bankCode
      });
      if (!response.data?.success) {
        throw new Error(response.data?.message || '완료 처리 실패');
      }
    }
    await loadData(true);
  } catch (error: any) {
    alert(`오류: ${error.response?.data?.message || error.message}`);
    await loadData(true);
  } finally {
    isProcessing.value = false;
  }
};

const goToDetail = (item: SummaryItem) => {
  router.push({
    name: 'memberDetailBankbook',
    params: { memberCode: memberCode.value },
    query: {
      year: item.year.toString(),
      month: item.month.toString(),
      bank: item.bankCode
    }
  });
};

// 메모 관련
const openMemoDialog = (item: SummaryItem) => {
  memoDialog.value = {
    show: true,
    year: item.year,
    month: item.month,
    bankCode: item.bankCode,
    bankAlias: item.bankAlias,
    memo: item.memo || ''
  };
};

const closeMemoDialog = () => {
  memoDialog.value.show = false;
};

const saveMemo = async () => {
  if (!memberCode.value) {
    return;
  }
  if (isSavingMemo.value) {
    return;
  }
  
  isSavingMemo.value = true;
  
  try {
    const response = await closingApi.updateMemo(memberCode.value, {
      year: memoDialog.value.year,
      month: memoDialog.value.month,
      bankCode: memoDialog.value.bankCode,
      memo: memoDialog.value.memo,
      bizGubun: accountingBizGubun.value
    });

    if (response.data?.success) {
      closeMemoDialog();
      await loadData(true);
    } else {
      throw new Error(response.data?.message || '메모 저장 실패');
    }
  } catch (error: any) {
    console.error('메모 저장 오류:', error);
    alert(`메모 저장 실패: ${error.response?.data?.message || error.message}`);
  } finally {
    isSavingMemo.value = false;
  }
};

// 이력 관련
const openHistoryDialog = async (item: SummaryItem) => {
  historyDialog.value = {
    show: true,
    year: item.year,
    month: item.month,
    bankCode: item.bankCode,
    bankAlias: item.bankAlias
  };
  
  await loadHistory(item.year, item.month, item.bankCode);
};

const closeHistoryDialog = () => {
  historyDialog.value.show = false;
  historyList.value = [];
};

const loadHistory = async (year: number, month: number, bankCode: string) => {
  if (!memberCode.value) return;
  
  isLoadingHistory.value = true;
  
  try {
    const response = await closingApi.getHistory(memberCode.value, {
      year,
      month,
      bankCode
    });
    
    if (response.data?.success) {
      historyList.value = response.data.data || [];
    }
  } catch (error: any) {
    console.error('이력 조회 오류:', error);
    historyList.value = [];
  } finally {
    isLoadingHistory.value = false;
  }
};

const getHistoryIcon = (action: string): string => {
  switch (action) {
    case 'request': return 'mdi-send';
    case 'complete': return 'mdi-check-circle';
    case 'cancel': return 'mdi-close-circle';
    case 'type_change': return 'mdi-swap-horizontal';
    case 'revert': return 'mdi-undo';
    default: return 'mdi-circle-small';
  }
};

const getHistoryActionLabel = (action: string, requestType: string | null): string => {
  const typeLabel = requestType ? HEUMIEUM_REQUEST_TYPES[requestType as HeumieumRequestType]?.label || requestType : '';
  
  switch (action) {
    case 'request': return `${typeLabel}요청`;
    case 'complete': return `${typeLabel}완료`;
    case 'cancel': return `${typeLabel}취소`;
    case 'type_change': return `타입 변경 → ${typeLabel}`;
    case 'revert': return '요청중으로 되돌림';
    default: return action;
  }
};

const formatDateTime = (dateStr: string): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hour = String(d.getHours()).padStart(2, '0');
  const min = String(d.getMinutes()).padStart(2, '0');
  return `${year}.${month}.${day} ${hour}:${min}`;
};

watch(accountingBizGubun, () => {
  loadData();
});

onMounted(() => {
  loadData();
});
</script>

<style lang="scss">
@import '@/assets/styles/pages/closing-history';
</style>
